/**
 * RoadmapThemesEditor Component
 *
 * Entity-specific editor for Roadmap Themes lookup table
 * Includes sequence ordering and roadmap filter
 */

import {
  useRoadmapThemes,
  useCreateRoadmapTheme,
  useUpdateRoadmapTheme,
  useDeleteRoadmapTheme,
  useReorderRoadmapTheme,
  useRoadmaps,
} from '@medsuite/api-client';
import { Button, Alert, Spinner, useDialog } from '@medsuite/ui-components';
import { useState, useEffect } from 'react';

interface RoadmapTheme {
  roadmap_theme_id: number;
  roadmap_id: number;
  theme_title: string;
  theme_context: string | null;
  theme_sequence: number;
}

interface RoadmapThemesEditorProps {
  onDataLoaded?: (count: number) => void;
}

export default function RoadmapThemesEditor({ onDataLoaded }: RoadmapThemesEditorProps) {
  const [selectedRoadmapId, setSelectedRoadmapId] = useState<number | undefined>(undefined);
  const { data: themes = [], isLoading, error } = useRoadmapThemes(selectedRoadmapId);
  const { data: roadmaps = [] } = useRoadmaps();
  const addMutation = useCreateRoadmapTheme();
  const updateMutation = useUpdateRoadmapTheme();
  const deleteMutation = useDeleteRoadmapTheme();
  const reorderMutation = useReorderRoadmapTheme();

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItem, setNewItem] = useState({
    roadmap_id: 0,
    theme_title: '',
    theme_context: '',
    theme_sequence: 0,
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<Partial<RoadmapTheme>>({});

  // Dialog hooks
  const { confirm } = useDialog();

  // Sort by sequence
  const sortedData = [...(themes as RoadmapTheme[])].sort((a, b) => a.theme_sequence - b.theme_sequence);

  // Notify parent of data count
  useEffect(() => {
    if (onDataLoaded && themes) {
      onDataLoaded((themes as RoadmapTheme[]).length);
    }
  }, [themes, onDataLoaded]);

  // Set default roadmap when roadmaps load
  useEffect(() => {
    if (roadmaps.length > 0 && !selectedRoadmapId) {
      setSelectedRoadmapId(roadmaps[0].roadmap_id);
    }
  }, [roadmaps, selectedRoadmapId]);

  // Update new item roadmap when selected roadmap changes
  useEffect(() => {
    if (selectedRoadmapId && newItem.roadmap_id !== selectedRoadmapId) {
      setNewItem((prev) => ({ ...prev, roadmap_id: selectedRoadmapId }));
    }
  }, [selectedRoadmapId, newItem.roadmap_id]);

  // Calculate next sequence number
  const getNextSequence = () => {
    if (sortedData.length === 0) {
      return 0;
    }
    return Math.max(...sortedData.map((t) => t.theme_sequence)) + 1;
  };

  // Handle add
  const handleSaveAdd = async () => {
    if (!selectedRoadmapId) {
      return;
    }
    try {
      await addMutation.mutateAsync({
        roadmap_id: selectedRoadmapId,
        theme_title: newItem.theme_title,
        theme_context: newItem.theme_context || null,
        theme_sequence: newItem.theme_sequence || getNextSequence(),
      });
      setIsAddingNew(false);
      setNewItem({
        roadmap_id: selectedRoadmapId,
        theme_title: '',
        theme_context: '',
        theme_sequence: 0,
      });
    } catch (err) {
      console.error('Failed to add:', err);
    }
  };

  // Handle edit
  const handleSaveEdit = async () => {
    if (!editingId) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        data: {
          roadmap_id: editingItem.roadmap_id,
          theme_title: editingItem.theme_title,
          theme_context: editingItem.theme_context,
          theme_sequence: editingItem.theme_sequence,
        },
      });
      setEditingId(null);
      setEditingItem({});
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: 'Delete Theme',
      message:
        'Are you sure you want to delete this theme? All items in this theme will also be deleted.',
      variant: 'danger',
      confirmText: 'Delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // Handle reorder
  const handleReorder = async (id: number, direction: 'up' | 'down') => {
    try {
      await reorderMutation.mutateAsync({ id, direction });
    } catch (err) {
      console.error('Failed to reorder:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load roadmap themes: {error.message}</Alert>;
  }

  return (
    <div className="space-y-4">
      {/* Roadmap Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label
            htmlFor="roadmap-filter"
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Filter by Roadmap:
          </label>
          <select
            id="roadmap-filter"
            value={selectedRoadmapId || ''}
            onChange={(e) =>
              setSelectedRoadmapId(e.target.value ? parseInt(e.target.value) : undefined)
            }
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
          >
            <option value="">All Roadmaps</option>
            {roadmaps.map((roadmap) => (
              <option key={roadmap.roadmap_id} value={roadmap.roadmap_id}>
                {roadmap.roadmap_title} ({roadmap.company_name})
              </option>
            ))}
          </select>
        </div>
        {!isAddingNew && selectedRoadmapId && (
          <Button variant="primary" onClick={() => setIsAddingNew(true)}>
            Add New
          </Button>
        )}
      </div>

      {!selectedRoadmapId ? (
        <Alert variant="info">Please select a roadmap to manage themes.</Alert>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Context
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Sequence
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Order
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Add New Row */}
              {isAddingNew && (
                <tr className="bg-blue-50 dark:bg-blue-950">
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                      Auto-generated
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <input
                      type="text"
                      value={newItem.theme_title}
                      onChange={(e) => setNewItem({ ...newItem, theme_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                      placeholder="Theme title"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <input
                      type="text"
                      value={newItem.theme_context}
                      onChange={(e) => setNewItem({ ...newItem, theme_context: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                      placeholder="Optional context"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <input
                      type="number"
                      value={newItem.theme_sequence || getNextSequence()}
                      onChange={(e) =>
                        setNewItem({ ...newItem, theme_sequence: parseInt(e.target.value) })
                      }
                      className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                    />
                  </td>
                  <td className="px-6 py-4"></td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setIsAddingNew(false)}
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mr-3"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAdd}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                    >
                      Save
                    </button>
                  </td>
                </tr>
              )}

              {/* Data Rows */}
              {sortedData.map((item, index) => {
                const isEditing = editingId === item.roadmap_theme_id;
                const isFirst = index === 0;
                const isLast = index === sortedData.length - 1;

                return (
                  <tr
                    key={item.roadmap_theme_id}
                    className={isEditing ? 'bg-yellow-50 dark:bg-yellow-950' : ''}
                  >
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      {item.roadmap_theme_id}
                    </td>
                    <td className="px-6 py-4">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingItem.theme_title || ''}
                          onChange={(e) =>
                            setEditingItem({ ...editingItem, theme_title: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                        />
                      ) : (
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {item.theme_title}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingItem.theme_context || ''}
                          onChange={(e) =>
                            setEditingItem({ ...editingItem, theme_context: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                        />
                      ) : (
                        <div
                          className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                          title={item.theme_context || ''}
                        >
                          {item.theme_context || '—'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {isEditing ? (
                        <input
                          type="number"
                          value={editingItem.theme_sequence ?? ''}
                          onChange={(e) =>
                            setEditingItem({
                              ...editingItem,
                              theme_sequence: parseInt(e.target.value),
                            })
                          }
                          className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                        />
                      ) : (
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {item.theme_sequence}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleReorder(item.roadmap_theme_id, 'up')}
                          disabled={isFirst || isEditing}
                          className="p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M4.5 15.75l7.5-7.5 7.5 7.5"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleReorder(item.roadmap_theme_id, 'down')}
                          disabled={isLast || isEditing}
                          className="p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => setEditingId(null)}
                            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mr-3"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                          >
                            Save
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditingId(item.roadmap_theme_id);
                              setEditingItem(item);
                            }}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item.roadmap_theme_id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}

              {/* Empty State */}
              {sortedData.length === 0 && !isAddingNew && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No themes found for this roadmap. Click &quot;Add New&quot; to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
